import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, Filter, Calendar, Tag, User, Clock, Eye, 
  ArrowLeft, ChevronDown, Grid, List, TrendingUp,
  BookOpen, Brain, Lightbulb, Target, Menu, X, LogOut,
  ChevronRight
} from 'lucide-react';
import { useFeaturedAnalyses } from '../hooks/useFeaturedAnalyses';
import { useAuth } from '../contexts/AuthContext';
import UnifiedNavigation from '../components/common/UnifiedNavigation';
import BreadcrumbNavigation from '../components/common/BreadcrumbNavigation';
import SEOHead from '../components/common/SEOHead';
import SkeletonLoader from '../components/common/SkeletonLoader';

interface FilterOptions {
  category: string;
  template: string;
  sortBy: 'latest' | 'popular' | 'title';
  viewMode: 'grid' | 'list';
}

const AnalysisListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: analyses, error, isLoading } = useFeaturedAnalyses({ limit: 50 });
  
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    category: searchParams.get('category') || 'all',
    template: searchParams.get('template') || 'all',
    sortBy: (searchParams.get('sortBy') as FilterOptions['sortBy']) || 'latest',
    viewMode: (searchParams.get('viewMode') as FilterOptions['viewMode']) || 'grid'
  });

  // 更新URL参数
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (filters.category !== 'all') params.set('category', filters.category);
    if (filters.template !== 'all') params.set('template', filters.template);
    if (filters.sortBy !== 'latest') params.set('sortBy', filters.sortBy);
    if (filters.viewMode !== 'grid') params.set('viewMode', filters.viewMode);
    
    setSearchParams(params, { replace: true });
  }, [searchTerm, filters, setSearchParams]);

  // 获取唯一的分类和模板选项
  const categories = analyses ? [...new Set(analyses.map(a => a.category).filter(Boolean))] : [];
  const templates = analyses ? [...new Set(analyses.map(a => a.template_name).filter(Boolean))] : [];

  // 过滤和排序分析数据
  const filteredAnalyses = analyses ? analyses.filter(analysis => {
    const matchesSearch = !searchTerm || 
      analysis.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      analysis.summary.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filters.category === 'all' || analysis.category === filters.category;
    const matchesTemplate = filters.template === 'all' || analysis.template_name === filters.template;
    
    return matchesSearch && matchesCategory && matchesTemplate;
  }).sort((a, b) => {
    switch (filters.sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'popular':
        // 假设按创建时间排序作为受欢迎程度的代理
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'latest':
      default:
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
  }) : [];

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case '科技': return <Brain className="w-4 h-4 text-white" />;
      case '商业': return <TrendingUp className="w-4 h-4 text-white" />;
      case '社会': return <User className="w-4 h-4 text-white" />;
      default: return <BookOpen className="w-4 h-4 text-white" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">加载失败</h2>
          <p className="text-gray-600 mb-4">无法加载案例数据，请稍后重试。</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="案例分析列表 - LeapWard深度思考平台"
        description="浏览所有精选案例分析，学习不同的思维方法和分析技巧，提升批判性思维能力。"
        keywords="案例分析,批判性思维,深度分析,LeapWard,SWOT分析,5W1H分析,新闻分析"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* 顶部导航栏 */}
        <UnifiedNavigation currentPage="analyses" />

        {/* 页面内容区域 */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* 面包屑导航 */}
          <BreadcrumbNavigation 
            items={[
              { name: '案例分析', current: true }
            ]}
            className="mb-6"
          />

          {/* 页面标题和统计 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">案例分析</h1>
              <p className="text-gray-600">
                {isLoading ? '加载中...' : `共 ${filteredAnalyses.length} 个精选案例`}
              </p>
            </div>
            
            {/* 视图切换 */}
            <div className="flex items-center space-x-2 mt-4 sm:mt-0">
              <button
                onClick={() => handleFilterChange('viewMode', 'grid')}
                className={`p-2 rounded-lg transition-colors ${
                  filters.viewMode === 'grid'
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleFilterChange('viewMode', 'list')}
                className={`p-2 rounded-lg transition-colors ${
                  filters.viewMode === 'list'
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* 搜索和筛选栏 */}
          <div className="bg-white border-b rounded-lg shadow-sm mb-8">
            <div className="px-6 py-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* 搜索框 */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="搜索案例标题或内容..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* 筛选按钮 */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  筛选
                  <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${
                    showFilters ? 'rotate-180' : ''
                  }`} />
                </button>
              </div>

              {/* 筛选选项 */}
              {showFilters && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* 分类筛选 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        分类
                      </label>
                      <select
                        value={filters.category}
                        onChange={(e) => handleFilterChange('category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">全部分类</option>
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>

                    {/* 模板筛选 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        分析模板
                      </label>
                      <select
                        value={filters.template}
                        onChange={(e) => handleFilterChange('template', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">全部模板</option>
                        {templates.map(template => (
                          <option key={template} value={template}>{template}</option>
                        ))}
                      </select>
                    </div>

                    {/* 排序方式 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        排序方式
                      </label>
                      <select
                        value={filters.sortBy}
                        onChange={(e) => handleFilterChange('sortBy', e.target.value as FilterOptions['sortBy'])}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="latest">最新发布</option>
                        <option value="popular">最受欢迎</option>
                        <option value="title">标题排序</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 案例列表 */}
          {isLoading ? (
            <div className={filters.viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
            }>
              {[...Array(6)].map((_, index) => (
                <SkeletonLoader key={index} className="h-64" />
              ))}
            </div>
          ) : filteredAnalyses.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-400 text-6xl mb-4">🔍</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || filters.category !== 'all' || filters.template !== 'all' 
                  ? '未找到匹配的案例' 
                  : '暂无案例数据'
                }
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || filters.category !== 'all' || filters.template !== 'all'
                  ? '请尝试调整搜索条件或筛选选项'
                  : '精彩案例即将上线，敬请期待'
                }
              </p>
              {(searchTerm || filters.category !== 'all' || filters.template !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilters({
                      category: 'all',
                      template: 'all',
                      sortBy: 'latest',
                      viewMode: filters.viewMode
                    });
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  清除筛选条件
                </button>
              )}
            </div>
          ) : (
            <div className={filters.viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
            }>
              {filteredAnalyses.map((analysis) => (
                <Link
                  key={analysis.id}
                  to={`/official-analysis/${analysis.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`bg-white rounded-lg shadow-sm hover:shadow-xl hover:border-blue-300 hover:-translate-y-1 transition-all duration-300 border block group cursor-pointer ${
                    filters.viewMode === 'list' ? 'p-6' : 'overflow-hidden'
                  }`}
                >
                  {filters.viewMode === 'grid' ? (
                    // 网格视图
                    <>
                      {/* 顶部渐变条 */}
                      <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"></div>
                      
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded flex items-center justify-center">
                              {getCategoryIcon(analysis.category)}
                            </div>
                            <span className="text-sm text-blue-600 font-medium">
                              {analysis.category || '综合'}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {formatDate(analysis.created_at)}
                          </span>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200">
                          {analysis.title}
                        </h3>
                        
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {analysis.summary}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
                          <div className="flex items-center space-x-2">
                            <Tag className="w-3 h-3" />
                            <span>{analysis.template_name}</span>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <span className="flex items-center">
                              <Eye className="w-3 h-3 mr-1" />
                              {Math.floor(Math.random() * 1000) + 100}
                            </span>
                            <span className="px-2 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 rounded-full font-semibold">
                              {Math.floor(Math.random() * 5) + 3}分钟阅读
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    // 列表视图
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                        {getCategoryIcon(analysis.category)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm text-blue-600 font-medium">
                              {analysis.category || '综合'}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {formatDate(analysis.created_at)}
                            </span>
                          </div>
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-200">
                          {analysis.title}
                        </h3>
                        
                        <p className="text-gray-600 mb-3 line-clamp-2">
                          {analysis.summary}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <Tag className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-500">
                                {analysis.template_name}
                              </span>
                            </div>
                            <span className="flex items-center text-sm text-gray-500">
                              <Eye className="w-4 h-4 mr-1" />
                              {Math.floor(Math.random() * 1000) + 100} 阅读
                            </span>
                          </div>
                          
                          <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 rounded-full text-sm font-semibold">
                            {Math.floor(Math.random() * 5) + 3}分钟阅读
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* 底部提示 */}
        {!user && filteredAnalyses.length > 0 && (
          <div className="bg-blue-50 border-t">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  想要获得更多深度分析？
                </h3>
                <p className="text-gray-600 mb-4">
                  注册账号，解锁完整的AI点评、用户讨论和个人成长档案功能
                </p>
                <button
                  onClick={() => navigate('/', { state: { scrollTo: 'hero' } })}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  立即注册
                  <Target className="ml-2 w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AnalysisListPage;